// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useEffect, useState } from 'react';
import { Check, FileText, Info } from 'lucide-react';
import { createOrganization, createOrganizationProfile, listUsers, provisionOrganizer, updateOrganization, createPaymentAccount, deploymentCommission, deploymentTransactionFee, resolveDeployment } from '../../lib/backOfficeApi';
import { settlementCurrencyOf } from '../../constants/payoutDestinations';
import { cleanName } from '../../lib/format';
import { toast } from '../../lib/buses';
import { BankPicker } from '../../components/BankPicker';
import { findBank } from '../../data/banks';
import { Field, Input, Select, Textarea } from '../../ui/Form';
import { Modal } from '../../ui/Modal';
import { WizardFooter, WizardSteps } from '../../ui/Wizard';
import { BRAND } from '../../lib/brand';

const ONBOARD_STEPS = [
  { id: 'business',  label: 'Business' },
  { id: 'contacts',  label: 'Contacts' },
  { id: 'kyc',       label: 'KYC docs' },
  { id: 'settlement',label: 'Settlement' },
];
const ONBOARD_CATEGORIES = ['Sports', 'Music & Concerts', 'Festivals', 'Cinema', 'Conferences', 'Cultural', 'Tournaments', 'Other'];

/**
 * Only the keys that were answered. An absent fact and a negative one are different things:
 * `tin: ""` reads as "we asked and there is none", and written over an existing profile it
 * BLANKS the value that was there. Empty strings, empty arrays, empty objects, undefined and
 * null are all "not said" and are dropped; `false` and `0` are answers and are kept.
 */
const present = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => {
  if (v === undefined || v === null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
}));

/**
 * WHAT IS ACTUALLY REQUIRED TO WRITE DOWN AN ORGANIZER: their name. Nothing else.
 *
 * This wizard used to demand a legal entity name, an event category, a contact's full name and
 * role and email and phone, an internal account manager, a registration number, a TIN, two
 * uploaded documents, a bank, an account number, an account holder and a commission — before it
 * would record anything at all. A club arrives over WhatsApp as "new team account for Kibera
 * Soccer, admin 0713857619 / mansursaid658@gmail.com". That is a real, complete, useful fact
 * about the world, and the form could not hold it.
 *
 * A compulsory field whose value nobody has does not get left blank — it gets FILLED IN. The
 * operator types "N/A" in the TIN, ticks a document nobody uploaded, picks the first name in the
 * account-manager list, and the database fills with fiction that reads exactly like fact. The
 * requirement did not produce completeness; it produced lies, and it pushed the real record into
 * a WhatsApp thread where the platform cannot see it at all.
 *
 * SO: ONE RULE, and it decides everything on this screen —
 *
 *    An organizer is a name. Every further act — giving someone a login, recording a business
 *    profile, opening a payout account — happens only when the facts that CONSTITUTE that act
 *    are present, and the receipt says which of them happened.
 *
 * That is why nothing below is validated into existence and nothing below is invented. An email
 * is what a login IS, so with one the organizer owns their organization and without one it is
 * owned by the admin who created it — stated, not silently. A bank name plus an account number
 * is what a payout account IS, so a half-filled bank block opens nothing rather than opening an
 * account with empty strings in it. Facts arrive when they arrive; the record grows to meet them.
 */
/**
 * `initialName` — what the operator already typed somewhere else (the event wizard's organiser
 * search), so they do not type it twice. `onCreate(profile, { id, name })` hands the new
 * organisation back to whoever asked for it.
 */
export const OnboardOrganizerModal = ({ onClose, onCreate, initialName = '' }) => {
  const [step, setStep] = useState('business');
  const [visited, setVisited] = useState(() => new Set(['business']));
  const goto = (id) => { setStep(id); setVisited((v) => new Set(v).add(id)); };
  // Business
  const [legalName, setLegalName] = useState(initialName);
  const [tradingName, setTradingName] = useState('');
  // Not defaulted to Uganda. This console runs six tenants and most of this one's organizers are
  // Kenyan clubs; a prefilled country is an assertion nobody made, and it decides the payout
  // currency below.
  const [country, setCountry] = useState('');
  const [orgType, setOrgType] = useState('');
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [eventCategories, setEventCategories] = useState([]);
  // Contacts
  // role '' — a prefilled "Founder/Director" is a claim about a stranger's job title that nobody
  // made, and it was landing in the stored profile of every organizer onboarded here.
  const [primary, setPrimary] = useState({ name: '', email: '', phone: '', role: '' });
  const [finance, setFinance] = useState({ name: '', email: '', phone: '' });
  const [accountManager, setAccountManager] = useState('');
  // The platform's real admin accounts — see the field's comment. null = still loading.
  const [staff, setStaff] = useState(null);
  const [staffError, setStaffError] = useState('');
  useEffect(() => {
    let alive = true;
    listUsers({ filterField: 'role', filterValue: 'admin', limit: 100, sortBy: 'name', sortDirection: 'asc' })
      .then(({ users }) => alive && setStaff(
        (users || [])
          .map((u) => ({ id: u.id, label: u.name?.trim() || u.email }))
          .filter((u) => u.label)
          .sort((a, b) => a.label.localeCompare(b.label))))
      .catch((e) => { if (alive) { setStaff([]); setStaffError(e?.message || 'request failed'); } });
    return () => { alive = false; };
  }, []);
  // KYC
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [tin, setTin] = useState('');
  const [docCert, setDocCert] = useState(false);
  const [docDirectors, setDocDirectors] = useState(false);
  const [docTax, setDocTax] = useState(false);
  const [docId, setDocId] = useState(false);
  // Settlement
  const [bankName, setBankName] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [bankHolder, setBankHolder] = useState('');
  const [bankSwift, setBankSwift] = useState('');
  const [bankBranch, setBankBranch] = useState('');
  const [tier, setTier] = useState('Tier 3');
  // Blank = the platform's rate. It was a prefilled 8 that nothing read (it went into a description
  // blob); it is the organiser's real commission now — what its new events are created with.
  const [commissionPct, setCommissionPct] = useState('');
  const [feePct, setFeePct] = useState('');
  // The platform's own rates, for what a blank means — read once from the backend this console talks to.
  const [platformRates, setPlatformRates] = useState({ commission: deploymentCommission(), transactionFee: deploymentTransactionFee() });
  useEffect(() => { resolveDeployment().then(() => setPlatformRates({ commission: deploymentCommission(), transactionFee: deploymentTransactionFee() })); }, []);
  const pctText = (f) => (f == null ? '' : String(+(f * 100).toFixed(2)));
  const [payoutSchedule, setPayoutSchedule] = useState('T+2');
  const [submitting, setSubmitting] = useState(false);

  const STEPS = ONBOARD_STEPS;

  const CATEGORIES = ONBOARD_CATEGORIES;
  const toggleCategory = (c) => setEventCategories(cs => cs.includes(c) ? cs.filter(x => x !== c) : [...cs, c]);

  // cleanName, not trim: these names get pasted out of WhatsApp and arrive with invisible
  // characters that make the organizer un-findable by typing their own name.
  const organizerName = cleanName(tradingName) || cleanName(legalName);
  // SWIFT is the one thing still checked, and only when typed: an 8/11-character code is not a
  // preference, it is a format the bank file has to parse. Everything else is a fact or it is
  // absent.
  const swiftOk = !bankSwift.trim() || /^[A-Z0-9]{8}$|^[A-Z0-9]{11}$/.test(bankSwift.trim().toUpperCase());
  // What each act NEEDS in order to be that act — not what the form would like to have.
  const ownerEmail   = primary.email.trim();
  const willOwn      = /\S+@\S+\.\S+/.test(ownerEmail);          // a login is an email
  const willBank     = !!(bankName.trim() && bankAccount.trim()); // a payout account is a bank + a number
  const settleCurrency = settlementCurrencyOf(country);
  const canSubmit    = !!organizerName && swiftOk && (!willBank || !!settleCurrency);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      /**
       * THE ORGANIZER GETS THEIR OWN ORGANIZATION — created before the profile, owned by the
       * PRIMARY CONTACT rather than by whoever is running this wizard.
       *
       * Left to itself the profile endpoint mints the organization with `userId: user.id`, the
       * onboarding admin, and its own comment says the plan was to "invite the real primary
       * contact onto it afterward" — which nothing prompts. The issue is not that staff are on it:
       * every super admin is added as an `admin` member of every organization automatically
       * (auth.ts's afterCreateOrganization hook), so access is never the question. The issue is
       * that the ORGANIZER was on it nowhere, and could not sign in to their own business. This
       * wizard already collects their name and email on step 2 — everything needed to name the
       * owner correctly at the moment it is being asked for.
       *
       * Same call the event-request approval makes: find-or-create the account by email, then
       * create the organization owned by them. Two doors, one act.
       *
       * WITHOUT AN EMAIL there is no person to own it yet, so the organization is created owned
       * by the admin doing this — which is what better-auth does anyway — and the receipt says
       * so, so nobody believes a login went out. `name` is optional: an account is identified by
       * its email, and a name invented from the local part of an address is a guess about a real
       * person. provisionOrganizer already falls back to the address itself.
       */
      const metadata = {
        onboardedVia: 'back-office-wizard',
        ...(legalName.trim() ? { legalName: cleanName(legalName) } : {}),
        ...(country ? { country } : {}),
      };
      const provisioned = willOwn
        ? await provisionOrganizer({
            ...(cleanName(primary.name) ? { name: cleanName(primary.name) } : {}),
            email: ownerEmail,
            organizationName: organizerName,
            metadata,
          })
        : { organization: await createOrganization({ name: organizerName, metadata }), userCreated: false };

      const profile = await createOrganizationProfile({
        // Named explicitly so the profile attaches to the organization the ORGANIZER owns,
        // instead of the endpoint minting a second one owned by this admin.
        organizationId: provisioned.organization?.id,
        organizerName,
        ...(website.trim() ? { website: website.trim() } : {}),
        bio: description.trim(),
        // The backend stores `description` as a string column and rejects
        // objects/arrays; the back-office reads it back via parseMaybeJson, so
        // serialise the structured profile to a JSON string.
        //
        // ONLY WHAT WAS SAID. `present()` drops empty keys instead of recording `tin: ""` and
        // `documents: { taxClearance: false }` — an absent fact and a negative fact are not the
        // same thing, and a blob full of blanks is what makes a half-known organizer LOOK
        // half-filled-in rather than newly arrived.
        description: JSON.stringify(present({
          legalName: cleanName(legalName),
          tradingName: cleanName(tradingName),
          country,
          orgType,
          eventCategories: eventCategories.length ? eventCategories : undefined,
          primaryContact: present(primary),
          financeContact: present(finance),
          accountManager,
          registrationNumber: registrationNumber.trim(),
          tin: tin.trim(),
          documents: present({
            certificateOfIncorporation: docCert || undefined,
            directorsRegister: docDirectors || undefined,
            taxClearance: docTax || undefined,
            directorIds: docId || undefined,
          }),
          settlement: willBank ? present({
            bankName, bankAccount, bankHolder, bankSwift, bankBranch,
            tier, commissionPct, payoutSchedule, currency: settleCurrency,
          }) : undefined,
        })),
      });
      // The organization was created above by `provisionOrganizer` (better-auth's own
      // `organization.create`, then add-member so the organizer owns it too); this profile
      // attaches to it. Everything above this point was ALSO getting silently stuffed into
      // the profile's `description` JSON blob and nowhere else — the real Profile tab reads
      // org.metadata (not this blob) and the real Banking tab reads PaymentAccount rows (not
      // this blob either), so business details and the bank account never actually showed up
      // anywhere a human would look for them. Write them to the real places too.
      const organizationId = profile?.organizationId;
      const warnings = [];
      const did = [];
      if (organizationId) {
        // Field names match OrgProfileTab.jsx's FIELDS list exactly — that's the real reader.
        // Sent only where there is something to send: writing `taxId: ""` over a field would
        // BLANK it on a re-save, and here it would make a brand-new organizer's profile tab read
        // as seven empty answers rather than one known name.
        const orgMeta = present({
          email: ownerEmail,
          phone: primary.phone.trim(),
          website: website.trim(),
          legalName: cleanName(legalName),
          taxId: tin.trim(),
          regNo: registrationNumber.trim(),
          bio: description.trim(),
        });
        const rateOf = (pct) => (String(pct).trim() === '' ? undefined : Math.round(Number(pct) * 100) / 10000);
        const terms = { ...(rateOf(commissionPct) !== undefined ? { commission: rateOf(commissionPct) } : {}), ...(rateOf(feePct) !== undefined ? { transactionFee: rateOf(feePct) } : {}) };
        if (Object.keys(orgMeta).length || Object.keys(terms).length) {
          await updateOrganization({ organizationId, data: { metadata: { ...orgMeta, ...terms } } })
            .then(() => did.push(Object.keys(terms).length ? `business profile and its terms (${[terms.commission != null && `${commissionPct}% commission`, terms.transactionFee != null && `${feePct}% fee`].filter(Boolean).join(', ')})` : 'business profile'))
            .catch((e) => warnings.push(`Business profile: ${e?.message || 'not saved'}`));
        }

        // A PAYOUT ACCOUNT IS A BANK AND A NUMBER. Without both it is not a half-account, it is
        // no account — this used to open one with empty strings in every field and `isDefault`
        // true, so a destination that could never receive money became the one payouts default
        // to. And its currency comes from the country, never a hardcoded 'UGX': most organizers
        // on this tenant are Kenyan clubs.
        if (willBank) {
          await createPaymentAccount({
            organizationId,
            name: `${organizerName} · ${bankName}`,
            type: 'bank_account',
            bankName,
            accountNumber: bankAccount,
            accountName: bankHolder.trim() || organizerName,
            swift: bankSwift.trim().toUpperCase() || findBank(bankName)?.swift || '',
            branch: bankBranch.trim(),
            currency: settleCurrency,
            isDefault: true,
          })
            .then(() => did.push(`payout account (${settleCurrency})`))
            .catch((e) => warnings.push(`Payout account: ${e?.message || 'not saved'}`));
        }
      } else {
        warnings.push('No organization id returned — business profile and payout account were not saved.');
      }

      if (onCreate) onCreate(profile, organizationId ? { id: String(organizationId), name: organizerName } : null);
      // THE RECEIPT NAMES WHAT HAPPENED, not what the form is called. With everything optional,
      // two submissions of this wizard can do very different amounts, and the operator has to be
      // able to tell which one they just did — "Organizer created" over a record with no login
      // is how somebody ends up waiting for a welcome email that was never sent.
      did.unshift(willOwn
        ? `owned by ${ownerEmail}${provisioned.userCreated ? ' (new account)' : ' (existing account)'}`
        : 'owned by you — no contact email yet, so no sign-in was created');
      if (warnings.length) {
        toast('Organizer created with gaps', 'warning', [...did, ...warnings].join(' · '));
      } else {
        toast('Organizer created', 'success', `${organizerName} · ${did.join(' · ')}`);
      }
      onClose();
    } catch (error) {
      toast('Organizer profile failed', 'error', error?.message || 'Backend rejected the profile');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={true} onClose={onClose} width="max-w-[760px]" title="Create organizer profile" sub="Only a name is needed. An email gives them their own sign-in (OTP, no password to send); a bank and account number open a payout account. Anything you don't have yet can be added later.">
      {/* Navigable, because nothing gates: an operator who has only a bank account to record
          should land on Settlement, not press Continue through three screens they can't fill. */}
      <WizardSteps steps={STEPS} current={step} onStep={goto} visited={visited} />

      {step === 'business' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {/* ONE of these two is the organizer's name. Neither is individually required —
                a club known only as "Kibera Soccer Women" has a trading name and no registered
                entity, and demanding a legal name would have got one invented. */}
            <Field label="Legal entity name" optional sub={organizerName ? undefined : 'Or a trading name below — the organizer needs one of the two'}><Input value={legalName} onChange={e => setLegalName(e.target.value)} placeholder="Akibua Sports Ltd" /></Field>
            <Field label="Trading name" optional sub="Customer-facing name if different from legal"><Input value={tradingName} onChange={e => setTradingName(e.target.value)} placeholder="Akibua Sports" /></Field>
            <Field label="Country of incorporation" optional sub={settleCurrency ? `Payouts settle in ${settleCurrency}` : 'Sets the payout currency'}>
              <Select value={country} onChange={e => setCountry(e.target.value)}>
                <option value="">Not stated</option>
                {['Uganda','Kenya','Tanzania','Rwanda','Other'].map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Entity type" optional>
              <Select value={orgType} onChange={e => setOrgType(e.target.value)}>
                <option value="">Not stated</option>
                <option value="company">Limited company</option>
                <option value="ngo">NGO / Non-profit</option>
                <option value="sole">Sole proprietor</option>
                <option value="partnership">Partnership</option>
                <option value="govt">Government / Public body</option>
              </Select>
            </Field>
            <Field label="Website" optional><Input value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://akibua.ug" /></Field>
          </div>
          <Field label="What kind of events?" optional sub="Pick all that apply · drives default risk profile and category routing">
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map(c => {
                const on = eventCategories.includes(c);
                return (
                  <button key={c} onClick={() => toggleCategory(c)} className={`h-7 px-2.5 text-[11.5px] rounded-full border ${on ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'bg-white text-[#1F2937] border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
                    {on && <Check className="inline w-3 h-3 mr-1 -mt-0.5" />}{c}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Description" optional sub="Short bio · shown on organizer page">
            <Textarea rows={2} value={description} onChange={e => setDescription(e.target.value)} placeholder="Akibua promotes UPL football fixtures and friendlies across Uganda…" />
          </Field>
        </div>
      )}

      {step === 'contacts' && (
        <div className="space-y-3">
          <div>
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Primary contact</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Full name" optional sub="Leave blank if all you have is the address"><Input value={primary.name} onChange={e => setPrimary({ ...primary, name: e.target.value })} placeholder="Daniel Mwesigwa" /></Field>
              <Field label="Role" optional>
                <Select value={primary.role} onChange={e => setPrimary({ ...primary, role: e.target.value })}>
                  <option value="">Not stated</option>
                  {['Founder/Director', 'CEO/MD', 'Events Manager', 'General Manager', 'Operations Lead'].map(r => <option key={r} value={r}>{r}</option>)}
                </Select>
              </Field>
              {/* The email is not required, but it is the ONE field that changes what this wizard
                  does — so the sub-label states the consequence either way instead of an asterisk
                  that says only "you must". */}
              <Field label="Email" optional sub={willOwn ? 'Their sign-in · a welcome email goes here' : 'Without one, no sign-in is created and you own the organization'}>
                <Input type="email" value={primary.email} onChange={e => setPrimary({ ...primary, email: e.target.value })} placeholder="daniel@akibua.ug" />
              </Field>
              <Field label="Phone" optional><Input type="tel" value={primary.phone} onChange={e => setPrimary({ ...primary, phone: e.target.value })} placeholder="e.g. +254 712 345 678" /></Field>
            </div>
          </div>
          <div className="border-t border-[#EEEDEA] pt-3">
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Finance contact <span className="font-normal normal-case text-[#9CA3AF]">(optional · for payouts and tax)</span></div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Name"><Input value={finance.name} onChange={e => setFinance({ ...finance, name: e.target.value })} placeholder="Esther Nakimuli" /></Field>
              <Field label="Email"><Input type="email" value={finance.email} onChange={e => setFinance({ ...finance, email: e.target.value })} placeholder="finance@akibua.ug" /></Field>
              <Field label="Phone"><Input type="tel" value={finance.phone} onChange={e => setFinance({ ...finance, phone: e.target.value })} placeholder="e.g. +254 712 345 678" /></Field>
            </div>
          </div>
          <div className="border-t border-[#EEEDEA] pt-3">
            {/* REAL COLLEAGUES OR NONE. This was a required pick from five invented people
                ("Brian O. (Account mgr)", "Esther N. (Risk)"…), so every organizer ever onboarded
                through this console was assigned to somebody who does not exist. The roster is now
                the platform's actual admin accounts, and it is optional — an unassigned
                relationship is a true statement; a fictional owner is not. */}
            <Field label={`${BRAND.name} account manager`} optional sub={
              staffError ? `Could not load platform staff — ${staffError}`
                : staff === null ? 'Loading platform staff…'
                : staff.length ? 'Internal owner for the relationship'
                : 'No platform admin accounts found to assign'}>
              <Select value={accountManager} onChange={e => setAccountManager(e.target.value)} disabled={!staff?.length}>
                <option value="">Unassigned</option>
                {(staff || []).map(s => <option key={s.id} value={s.label}>{s.label}</option>)}
              </Select>
            </Field>
          </div>
        </div>
      )}

      {step === 'kyc' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Company registration number" optional sub="URSB / equivalent"><Input value={registrationNumber} onChange={e => setRegistrationNumber(e.target.value)} placeholder="URSB-2024-9817" /></Field>
            <Field label="Tax Identification Number (TIN)" optional><Input value={tin} onChange={e => setTin(e.target.value)} placeholder="1000-XXX-XXX" /></Field>
          </div>
          <div>
            {/* NOT "Required documents", and the button no longer says Upload. Nothing here
                uploads anything — the summary on the last step has always admitted as much, and
                the real files are attached on the organizer's KYC Documents tab. These four
                switches record what has been COLLECTED offline, so that is what they say. A
                control that claims to do more than it does is how a tick ends up standing in for
                a certificate nobody has. */}
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Documents collected <span className="font-normal normal-case text-[#9CA3AF]">— a checklist, not an upload</span></div>
            <div className="space-y-1.5">
              {[
                { id: 'cert', label: 'Certificate of incorporation', checked: docCert, setter: setDocCert },
                { id: 'directors', label: 'Directors / shareholders register', checked: docDirectors, setter: setDocDirectors },
                { id: 'tax', label: 'Tax clearance certificate', checked: docTax, setter: setDocTax },
                { id: 'id', label: 'Directors\' national IDs / passports', checked: docId, setter: setDocId },
              ].map(d => (
                <div key={d.id} className={`flex items-center justify-between px-3 py-2 rounded-md border ${d.checked ? 'border-[#1F6A3A] bg-[#F0F9F2]' : 'border-[#E6E4E1] bg-white'}`}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className={`w-7 h-7 rounded-full ${d.checked ? 'bg-[#1F6A3A] text-white' : 'bg-[#F2F1ED] text-[#9CA3AF]'} flex items-center justify-center flex-shrink-0`}>{d.checked ? <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> : <FileText className="w-3.5 h-3.5" />}</div>
                    <div className="min-w-0">
                      <div className="text-[12px] font-medium text-[#1F2937]">{d.label}</div>
                      <div className="text-[10.5px] text-[#6B7280] mt-0.5">{d.checked ? 'Held offline · attach the file on the KYC Documents tab' : 'Not collected yet'}</div>
                    </div>
                  </div>
                  {!d.checked ? (
                    <button onClick={() => d.setter(true)} className="h-7 px-2.5 text-[11px] font-medium rounded-md bg-white border border-[#E6E4E1] text-[#1F2937] hover:bg-[#F2F1ED] inline-flex items-center gap-1"><Check className="w-3 h-3" />Mark collected</button>
                  ) : (
                    <button onClick={() => d.setter(false)} className="h-7 px-2.5 text-[11px] font-medium rounded-md text-[#6B7280] hover:bg-[#F2F1ED]">Undo</button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#FFF7E6] border border-[#FAE2A8] rounded-md px-3 py-2.5 text-[11.5px] text-[#8A5A12] flex items-start gap-2">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>KYC review takes 24–48h. The organizer can finish profile setup in the meantime but cannot publish events until KYC is approved.</div>
          </div>
        </div>
      )}

      {step === 'settlement' && (
        <div className="space-y-3">
          <div>
            {/* A payout account is a bank and an account number. Either alone opens nothing —
                said here rather than enforced with asterisks, because leaving this whole block
                empty is a perfectly good answer for a club that has not given its bank yet. */}
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Bank account for payouts <span className="font-normal normal-case text-[#9CA3AF]">— {willBank ? (settleCurrency ? `opens a ${settleCurrency} payout account` : 'set the country above so payouts have a currency') : 'needs a bank and an account number, or leave blank'}</span></div>
            <Field label="Bank" optional sub="Search the known-banks directory — picking one fills its SWIFT profile">
              <BankPicker
                value={bankName}
                onChange={(v) => { setBankName(v); if (!bankSwift) { const k = findBank(v); if (k?.swift) setBankSwift(k.swift); } }}
                onProfile={(b) => { if (b?.swift && !bankSwift) setBankSwift(b.swift); }}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="Account holder" optional sub={bankHolder.trim() ? 'Name on the bank statement — this is what payouts show' : `Defaults to ${organizerName || 'the organizer name'}`}><Input value={bankHolder} onChange={e => setBankHolder(e.target.value)} placeholder={organizerName || legalName || 'Akibua Sports Ltd'} /></Field>
              <Field label="Account number" optional><Input value={bankAccount} onChange={e => setBankAccount(e.target.value)} placeholder="9030001234567" /></Field>
              <Field label="SWIFT / BIC" optional sub={findBank(bankName)?.swift ? `Directory default ${findBank(bankName).swift} — override per branch` : '8 or 11 characters · verify with the bank'}>
                <Input value={bankSwift} onChange={e => setBankSwift(e.target.value.toUpperCase().slice(0, 11))} placeholder={findBank(bankName)?.swift || 'e.g. SBICUGKX'} />
                {!swiftOk && <div className="text-[11px] text-[#9B1F1F] mt-1">SWIFT must be 8 or 11 characters · letters and numbers only</div>}
              </Field>
              <Field label="Branch" optional><Input value={bankBranch} onChange={e => setBankBranch(e.target.value)} placeholder="Kampala Main" /></Field>
            </div>
          </div>
          <div className="border-t border-[#EEEDEA] pt-3">
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Commercial terms</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Platform commission" optional sub="Kept from its sales. Blank = platform rate">
                <div className="relative"><Input inputMode="decimal" value={commissionPct} onChange={e => setCommissionPct(e.target.value.replace(/[^0-9.]/g, ''))} placeholder={pctText(platformRates.commission)} /><span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-[#6B7280]">%</span></div>
              </Field>
              <Field label="Transaction fee" optional sub="Buyer pays on top. Blank = platform fee">
                <div className="relative"><Input inputMode="decimal" value={feePct} onChange={e => setFeePct(e.target.value.replace(/[^0-9.]/g, ''))} placeholder={pctText(platformRates.transactionFee)} /><span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-[#6B7280]">%</span></div>
              </Field>
              <Field label="Risk tier" sub="Drives velocity limits, KYC depth, recon cadence">
                <Select value={tier} onChange={e => setTier(e.target.value)}>
                  <option>Tier 1</option><option>Tier 2</option><option>Tier 3</option>
                </Select>
              </Field>
              <Field label="Payout schedule" sub="When proceeds are released">
                <Select value={payoutSchedule} onChange={e => setPayoutSchedule(e.target.value)}>
                  <option value="T+0">Same-day (T+0)</option>
                  <option value="T+1">Next business day (T+1)</option>
                  <option value="T+2">T+2 (default)</option>
                  <option value="weekly">Weekly</option>
                  <option value="manual">Manual release only</option>
                </Select>
              </Field>
            </div>
          </div>
          <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md p-3">
            {/* WHAT *THIS* SUBMISSION DOES. With every field optional, two runs of this wizard
                can do very different amounts of work, so the list is conditional — it describes
                the acts whose facts are actually present, and names the ones that are not
                happening rather than listing everything the wizard is capable of. */}
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-2">On submit</div>
            <ul className="text-[11.5px] text-[#4B5563] space-y-1 list-disc list-inside">
              <li>The organization <span className="text-[#1F2937]">{organizerName || '— name it on step 1'}</span> is created{willOwn
                ? <> and owned by <span className="text-[#1F2937]">{ownerEmail}</span>, who gets a sign-in email</>
                : <>, owned by you — <span className="text-[#1F2937]">no contact email</span>, so nobody outside the platform can sign in to it yet</>}</li>
              <li>{Object.keys(present({ email: ownerEmail, phone: primary.phone.trim(), website: website.trim(), legalName: cleanName(legalName), taxId: tin.trim(), regNo: registrationNumber.trim(), bio: description.trim() })).length
                ? 'What you filled in saves to the organizer’s real Profile tab — blanks are left blank, not written as empty'
                : 'Nothing to save to the Profile tab yet — it can be filled in there any time'}</li>
              <li>{willBank
                ? <>A real <span className="text-[#1F2937]">{settleCurrency || '(no currency — set the country)'}</span> payout account under Banking &amp; Payouts</>
                : 'No payout account — add one under Banking & Payouts when the bank details arrive'}</li>
              <li>Its events start on <span className="text-[#1F2937]">{String(commissionPct).trim() ? `${commissionPct}%` : `the platform's ${pctText(platformRates.commission)}%`}</span> commission and <span className="text-[#1F2937]">{String(feePct).trim() ? `${feePct}%` : `the platform's ${pctText(platformRates.transactionFee)}%`}</span> transaction fee — change them later on its Commission &amp; fees tab</li>
              <li>KYC documents are NOT uploaded here — this only records what is held offline. Attach the real files under the KYC Documents tab.</li>
            </ul>
          </div>
        </div>
      )}

      {/* The name is the only thing that can hold this back, plus two format facts: a SWIFT the
          bank file has to parse, and a currency for an account that is being opened. Say WHICH,
          because a disabled button with no reason is the thing operators file tickets about. */}
      {!canSubmit && (
        <div className="mt-3 text-[11.5px] text-[#8A5A12] bg-[#FFF7E6] border border-[#FAE2A8] rounded-md px-3 py-2 inline-flex items-start gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{!organizerName ? 'Give the organizer a name on step 1 — a legal entity name or a trading name. Everything else can wait.'
            : !swiftOk ? 'The SWIFT / BIC on Settlement is not 8 or 11 characters — correct it or clear it.'
            : 'A payout account needs a currency: set the country of incorporation on step 1, or clear the bank details.'}</span>
        </div>
      )}
      <WizardFooter
        onBack={step !== 'business' ? () => goto(STEPS[STEPS.findIndex(s => s.id === step) - 1].id) : null}
        onCancel={onClose}
        // Continue never refuses — nothing here is compulsory. The primary action becomes
        // "Create profile" on the last step, and the rail above skips to any step directly.
        onNext={() => {
          const idx = STEPS.findIndex(s => s.id === step);
          if (idx < STEPS.length - 1) goto(STEPS[idx + 1].id);
          else if (canSubmit) submit();
        }}
        nextLabel={step === 'settlement' ? (submitting ? 'Creating profile…' : 'Create profile') : 'Continue'}
        nextDisabled={submitting || (step === 'settlement' && !canSubmit)}
        nextReason={submitting || step !== 'settlement' ? null
          : !organizerName ? 'Name the organizer on step 1'
          : !swiftOk ? 'Fix or clear the SWIFT / BIC'
          : willBank && !settleCurrency ? 'Set the country, or clear the bank details' : null}
        primaryIcon={step === 'settlement' ? Check : null}
        // NOTHING HERE IS COMPULSORY BUT A NAME, so the moment the profile can be created it can be
        // created from any step — an operator who came from the event wizard to name a new client
        // should not press Continue three times past forms they have nothing to put in.
        secondary={step !== 'settlement' && canSubmit ? { label: submitting ? 'Creating profile…' : 'Create profile', onClick: submit, disabled: submitting } : null}
      />
    </Modal>
  );
};

/* ----------------------------------------------------------------------------
   CREATE BOX OFFICE WIZARD — replaces the inline modal in BoxOfficesListPage
   Covers scope (system/tournament/event), basics, tickets, payments, operators+credentials, printing
   ---------------------------------------------------------------------------- */
